/**
 * API client for workflow management.
 *
 * All endpoints target /api/workflows/ (the V2 workflow_router).
 * Includes:
 *   - Run lifecycle (create, get, list, events, cancel, report)
 *   - Export management (list, content, save, download)
 *   - IIQ prepared-file management
 *   - HITL gate actions (proceed, rerun with feedback, cancel from gate)
 *   - WebSocket ticket issuance
 *   - Database connection test
 */

import { apiFetch } from "@/lib/auth"

// ===========================================================================
// Shared Types
// ===========================================================================

export interface CreateRunRequest {
  mode: 'codebase' | 'database'
  name?: string
  config?: Record<string, unknown>
  notes?: string
}

export interface RunResponse {
  run_id: string
  status: string
  mode: string
  name?: string
  created_at: string
  websocket_url?: string
  notes?: string
  repo_url?: string
  base_branch?: string
}

export interface ExportFile {
  file: string
  size: number
  size_human: string
  records: number
  modified_at: string
  /** Raw CSV header columns (pre `_drop_null_columns`). Used by the Issues
   * tab in the post_transform HITL modal to detect genuinely missing
   * vendor-required columns vs columns that exist but are entirely blank. */
  columns?: string[]
  // Optional enrichment for privileged_identity_report.csv
  user_count?: number
  privileged_count?: number
}

export interface ExportsResponse {
  run_id: string
  exports: ExportFile[]
  total_files: number
}

export interface ExportContentResponse {
  file: string
  columns: string[]
  rows: Record<string, string>[]
  total_rows: number
  page: number
  page_size: number
}

export interface SaveExportsRequest {
  edit_count: number
  exports: Record<string, { columns: string[]; rows: Record<string, string>[] }>
}

export interface SaveExportsResponse {
  status: 'saved' | 'validation_failed' | 'conflict'
  commit_hash?: string
  commit_message?: string
  files_written?: number
  edit_count?: number
  validation_passed?: boolean
  errors?: Array<{ csv: string; row: number; column: string; message: string }>
  message?: string
  current_edit_count?: number
}

export interface IIQFile {
  file: string
  size: number
  size_human: string
  records: number
  type: 'csv' | 'xml'
}

export interface IIQPrepareResponse {
  run_id: string
  app_name: string
  remote_path: string
  files: IIQFile[]
}

// ===========================================================================
// Base URL helper
// ===========================================================================

function getApiBaseUrl(): string {
  return process.env.NEXT_PUBLIC_API_URL || ''
}

// ===========================================================================
// Run lifecycle
// ===========================================================================

export async function createRun(request: CreateRunRequest): Promise<RunResponse> {
  const apiUrl = getApiBaseUrl()
  const payload = {
    ...request,
    name: request.name || `${request.mode}-${new Date().toISOString().slice(0, 16).replace('T', '-')}`,
  }
  const response = await apiFetch(`${apiUrl}/api/workflows/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create run' }))
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

export async function cancelRun(runId: string): Promise<void> {
  const apiUrl = getApiBaseUrl()
  const response = await apiFetch(`${apiUrl}/api/workflows/${runId}/cancel/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to cancel run' }))
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`)
  }
}

export interface RetryStepResult {
  run_id: string
  status: string
  step_type: string
  attempt: number
}

/** Re-enqueue the most-recently-failed step on a failed workflow. Backend
 *  caps manual retries at 5/step and returns 409 if the cap is hit. */
export async function retryFailedStep(runId: string): Promise<RetryStepResult> {
  const apiUrl = getApiBaseUrl()
  const response = await apiFetch(`${apiUrl}/api/workflows/${runId}/retry/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to retry step' }))
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`)
  }
  return response.json()
}

export interface AppCredentialDeleteResult {
  success: boolean
  run_id: string
  app_name: string | null
  run_scoped_cleared: boolean
  app_scoped_cleared: boolean
}

/**
 * Delete the provisioning-generated client secret from Identity Matrix's
 * Vault. Wipes both the run-scoped record AND the cross-run app-scoped
 * record, so future runs of the same app no longer auto-detect a stored
 * secret. Idempotent.
 */
export async function deleteAppCredentialSecret(
  runId: string,
): Promise<AppCredentialDeleteResult> {
  const apiUrl = getApiBaseUrl()
  const response = await apiFetch(
    `${apiUrl}/api/workflows/${runId}/app-credentials/secret/`,
    { method: 'DELETE' },
  )
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete secret' }))
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`)
  }
  return response.json()
}

// ===========================================================================
// Exports
// ===========================================================================

export async function getRunExports(runId: string): Promise<ExportsResponse> {
  const apiUrl = getApiBaseUrl()
  const response = await apiFetch(`${apiUrl}/api/workflows/${runId}/exports/`)

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Exports not found' }))
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

export async function getExportContent(
  runId: string,
  filename: string,
  page: number = 1,
  pageSize: number = 500
): Promise<ExportContentResponse> {
  const apiUrl = getApiBaseUrl()
  const response = await apiFetch(
    `${apiUrl}/api/workflows/${runId}/exports/${encodeURIComponent(filename)}/?page=${page}&page_size=${pageSize}`
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Export content not found' }))
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

/**
 * Get ALL rows for a specific export file (no pagination).
 * Used by edit mode to load the full dataset into AG Grid.
 */
export async function getExportContentAll(
  runId: string,
  filename: string,
): Promise<ExportContentResponse> {
  const resp = await getExportContent(runId, filename, 1, 999999)
  if (resp.rows.length < resp.total_rows) {
    throw new Error(
      `Incomplete data: received ${resp.rows.length} of ${resp.total_rows} rows for ${filename}. ` +
      `Dataset may be too large for inline editing.`
    )
  }
  return resp
}

/**
 * Refresh the post_transform gate's per-vendor preflight payload server-side.
 *
 * Used when the modal opens against a gate whose `output_data.payload.preflight`
 * predates the per-user-skip extension (missing `incompatible_user_keys` /
 * `compatible_count`). The backend recomputes from the current users.csv and
 * broadcasts the refreshed status report so React Query / WorkflowStatusContext
 * picks up the new payload without a page reload.
 */
export async function refreshPostTransformPreflight(runId: string): Promise<void> {
  const apiUrl = getApiBaseUrl()
  await apiFetch(`${apiUrl}/api/workflows/${runId}/gates/post_transform/refresh-preflight/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
}

/**
 * Save edited export CSVs. The backend returns structured JSON for
 * success (200), validation failure (422), and optimistic-lock conflict (409).
 */
export async function saveExports(
  runId: string,
  data: SaveExportsRequest,
): Promise<SaveExportsResponse> {
  const apiUrl = getApiBaseUrl()
  const res = await apiFetch(`${apiUrl}/api/workflows/${runId}/exports/save/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })

  // 200, 409, 422 all return valid SaveExportsResponse JSON
  if (res.status === 200 || res.status === 409 || res.status === 422) {
    return res.json()
  }

  // Other errors (429, 500, etc.)
  const err = await res.text().catch(() => 'Unknown error')
  throw new Error(`Save failed (${res.status}): ${err}`)
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export async function downloadExportFile(runId: string, filename: string): Promise<void> {
  const apiUrl = getApiBaseUrl()
  const response = await apiFetch(
    `${apiUrl}/api/workflows/${runId}/exports/${encodeURIComponent(filename)}/download/`
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Download failed' }))
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`)
  }

  const blob = await response.blob()
  triggerBlobDownload(blob, filename)
}

export async function downloadAllExports(runId: string, format: 'xlsx' | 'zip' = 'xlsx'): Promise<void> {
  const apiUrl = getApiBaseUrl()
  const response = await apiFetch(
    `${apiUrl}/api/workflows/${runId}/exports/download-all/?format=${format}`
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Download failed' }))
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`)
  }

  const blob = await response.blob()
  const disposition = response.headers.get('Content-Disposition') || ''
  const match = disposition.match(/filename="?([^"]+)"?/)
  const filename = match?.[1] || `${runId}-exports.${format}`
  triggerBlobDownload(blob, filename)
}

/**
 * Download the IGA Queries.xlsx export (Saviynt generated queries). Single file
 * per workflow — the backend serves the latest IGA_EXPORT artifact.
 */
export async function downloadIgaQueries(runId: string): Promise<void> {
  const apiUrl = getApiBaseUrl()
  const response = await apiFetch(`${apiUrl}/api/workflows/${runId}/iga-queries/`)

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Download failed' }))
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`)
  }

  const blob = await response.blob()
  const disposition = response.headers.get('Content-Disposition') || ''
  const match = disposition.match(/filename="?([^"]+)"?/)
  const filename = match?.[1] || 'IGA Queries.xlsx'
  triggerBlobDownload(blob, filename)
}

// ===========================================================================
// IIQ prepared files
// ===========================================================================

export async function prepareIIQFiles(runId: string): Promise<IIQPrepareResponse> {
  const apiUrl = getApiBaseUrl()
  const response = await apiFetch(`${apiUrl}/api/workflows/${runId}/iiq-prepare/`, {
    method: 'POST',
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to prepare IIQ files' }))
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

export async function downloadIIQFile(runId: string, filename: string): Promise<void> {
  const apiUrl = getApiBaseUrl()
  const response = await apiFetch(
    `${apiUrl}/api/workflows/${runId}/iiq-files/${encodeURIComponent(filename)}/`
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Download failed' }))
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`)
  }

  const blob = await response.blob()
  triggerBlobDownload(blob, filename)
}

export async function downloadAllIIQFiles(runId: string): Promise<void> {
  const apiUrl = getApiBaseUrl()
  const response = await apiFetch(
    `${apiUrl}/api/workflows/${runId}/iiq-files/download-all/`
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Download failed' }))
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`)
  }

  const blob = await response.blob()
  triggerBlobDownload(blob, `iiq-files.zip`)
}

// ===========================================================================
// WebSocket Ticket
// ===========================================================================

export interface WsTicketResponse {
  ticket: string
  expires_in: number
}

/**
 * Fetch a single-use WebSocket ticket scoped to a workflow.
 * The ticket has a 30-second TTL and must be passed as ?ticket=<uuid>
 * in the WebSocket URL. The backend consumer validates and consumes it.
 */
export async function getWsTicket(workflowId: string): Promise<WsTicketResponse> {
  const response = await apiFetch('/api/workflows/ws/ticket/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ workflow_id: workflowId }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to get WebSocket ticket' }))
    throw new Error(error.detail || `HTTP ${response.status}`)
  }

  return response.json()
}

// ===========================================================================
// HITL Gate Actions
// ===========================================================================

export interface HitlProceedResponse {
  status: string
  next_step_id: string | null
  next_step_type: string | null
}

export interface ProceedGateBody {
  /**
   * Column-level override for post_classify proceed.
   * Each value is a fully-qualified `schema.table.column` identifier.
   * Backend schema: `HitlClassifyOverride` in apps/workflows/schemas.py.
   */
  override?: {
    primary_identifier: string
    group_membership: string
    group_name: string
    /**
     * Optional 4th role (Q-A: "What identifies a unique user of this
     * application?"). Absent → server derives identity_uuid =
     * primary_identifier. Backend: `HitlClassifyOverride.identity_uuid`.
     */
    identity_uuid?: string
  }
  /**
   * AM vendor kinds to drop from this run. Consumed by the post_transform
   * gate when per-vendor preflight surfaced incompatible targets and the
   * operator chose to proceed with the passing subset. Backend schema:
   * `HitlProceedInput.drop_targets` in apps/workflows/schemas.py.
   */
  drop_targets?: string[]
  /**
   * AM vendor kinds to keep, but with incompatible users excluded. Per-vendor
   * skip lists are derived from the gate's preflight payload. Disjoint with
   * `drop_targets`. Backend schema: `HitlProceedInput.skip_user_targets`.
   */
  skip_user_targets?: string[]
  /**
   * IGA early-mapping answers (post_classify only). Shape is validated
   * server-side in `_validate_iga_answers` against the gate's question ids
   * + the ranking/profile allowlist. Other gates ignore it silently.
   *
   *   iga_status:       a status-column answer
   *                     {status_model: "column", column, active: string[],
   *                      inactive: string[]}, OR a row-existence answer
   *                     {status_model: "row_existence"} (presence of the row
   *                     means the person is active — no column/value lists).
   *   iga_account_name: {profile_attribute, source}
   *
   * `source` is REQUIRED and one of canonical_field | vendor_property |
   * custom; for a column answer active/inactive must be NON-EMPTY; custom
   * names must match `^[a-z][a-z0-9_]*$`. Mirrors the modal's `IgaAnswers`
   * (components/classification-review-modal.tsx) and the server's
   * `_validate_iga_status_answer`. Backend: `HitlProceedInput.iga_answers`.
   */
  iga_answers?: {
    iga_status?:
      | {
          status_model: "column"
          column: string
          active: string[]
          inactive: string[]
        }
      | { status_model: "row_existence" }
    iga_account_name?: {
      profile_attribute: string
      source: "canonical_field" | "vendor_property" | "custom"
    }
  }
  /**
   * Operator-confirmed correlation chain (correlation_review gate only). Ordered
   * list — first match wins. Written back to `ranking.json["correlation_chain"]`
   * for the P3 builders. Backend: `HitlProceedInput.correlation_chain`.
   */
  correlation_chain?: {
    identity_type: string
    data_column: string | null
    platform_attribute: string | null
  }[]
}

/**
 * Draft selections for the in-progress classify review.
 * Sent via PATCH /api/workflows/{id}/classify/draft/ (rate limited to 30/min).
 */
export interface DraftSelectionsBody {
  primary_identifier: string | null
  group_membership: string | null
  group_name: string | null
}

export interface DraftSelectionsResponse {
  draft_selections: {
    primary_identifier?: string | null
    group_membership?: string | null
    group_name?: string | null
  }
}

/**
 * Persist partial HITL classify selections. Only non-null keys are merged
 * server-side; null values are treated as "leave as-is".
 */
export async function saveDraftSelections(
  workflowId: string,
  draft: Partial<DraftSelectionsBody>,
): Promise<DraftSelectionsResponse> {
  const response = await apiFetch(
    `/api/workflows/${workflowId}/classify/draft/`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(draft),
    },
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to save draft' }))
    throw new Error(formatDetail(error.detail) || `Draft save failed: ${response.status}`)
  }

  return response.json()
}

function formatDetail(detail: unknown): string {
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    // Ninja/Pydantic 422 shape: [{loc, msg, type}, ...].
    return detail
      .map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : JSON.stringify(d)))
      .join('; ')
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail)
  return ''
}

/**
 * Approve an HITL gate and advance the workflow to the next step.
 *
 * For post_classify gates, pass a `body` with the `override` field populated.
 * Other gates ignore the body silently.
 */
export async function proceedGate(
  workflowId: string,
  stepId: string,
  body?: ProceedGateBody,
): Promise<HitlProceedResponse> {
  const response = await apiFetch(
    `/api/workflows/${workflowId}/steps/${stepId}/proceed/`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    },
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to proceed gate' }))
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

export interface HitlRerunResponse {
  status: string
  new_step_id: string
  new_step_type: string
}

/**
 * Re-run the previous execution step with user feedback.
 */
export async function rerunWithFeedback(
  workflowId: string,
  stepId: string,
  feedback: string,
): Promise<HitlRerunResponse> {
  const response = await apiFetch(
    `/api/workflows/${workflowId}/steps/${stepId}/rerun/`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ feedback }),
    },
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to rerun with feedback' }))
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

// ===========================================================================
// Classify gate re-analysis
// ===========================================================================

export interface ReanalysisResponse {
  status: string
  new_step_id?: string
  attempt?: number
}

export class ReanalysisLimitExhaustedError extends Error {
  limit: number
  used: number

  constructor(limit: number, used: number) {
    super(`Re-analysis limit reached (${used}/${limit})`)
    this.name = "ReanalysisLimitExhaustedError"
    this.limit = limit
    this.used = used
  }
}

export class ReanalysisConflictError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "ReanalysisConflictError"
  }
}

/**
 * Request a re-run of the classify → profile → rank → gate chain.
 *
 * Backend guards:
 *   - 429 when the 5-per-workflow lifetime limit is exhausted.
 *   - 409 when a reanalysis is already in flight OR the gate is not at
 *     awaiting_review.
 */
export async function requestReanalysis(
  workflowId: string,
  body?: { reason?: string },
): Promise<ReanalysisResponse> {
  const response = await apiFetch(
    `/api/workflows/${workflowId}/classify/reanalyze/`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body ?? {}),
    },
  )

  if (response.status === 429) {
    const payload = await response.json().catch(() => ({}))
    throw new ReanalysisLimitExhaustedError(payload.limit ?? 5, payload.used ?? 5)
  }
  if (response.status === 409) {
    throw new ReanalysisConflictError(
      "Re-analysis already in progress or gate not at awaiting_review",
    )
  }
  if (!response.ok) {
    throw new Error(`Re-analysis failed: ${response.status} ${await response.text()}`)
  }
  return response.json()
}

export interface HitlCancelResponse {
  status: string
}

/**
 * Cancel the workflow from an HITL gate.
 */
export async function cancelFromGate(
  workflowId: string,
  stepId: string,
): Promise<HitlCancelResponse> {
  const response = await apiFetch(
    `/api/workflows/${workflowId}/steps/${stepId}/cancel/`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    },
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to cancel from gate' }))
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

// ===========================================================================
// Database connection test (used by ETL settings)
// ===========================================================================

export interface TestConnectionRequest {
  connection_method: "simple" | "connection_string"
  db_type: "postgresql" | "mssql" | "oracle" | "flatfile"
  db_host?: string
  db_port?: string
  db_name?: string
  db_service_name?: string
  db_user?: string
  db_password?: string
  connection_string?: string
}

export interface TestConnectionResponse {
  success: boolean
  error: string | null
}

export async function testDatabaseConnection(
  payload: TestConnectionRequest,
): Promise<TestConnectionResponse> {
  const response = await apiFetch(`/api/workflows/test-connection/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Test connection failed" }))
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}
